import {
    Component,
    EventEmitter,
    inject,
    Input,
    OnInit,
    Output,
    TemplateRef,
    ViewChild,
} from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { LookupService } from '../../../../services/lookup.service';
import { TimesheetService } from '../../../../services/timesheet.service';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import { CommonModule } from '@angular/common';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzTimePickerModule } from 'ng-zorro-antd/time-picker';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { TableModule } from 'primeng/table';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { UserService } from '../../../../services/user.service';

@Component({
    selector: 'ppl-timesheet-staff-errorcode',
    standalone: true,
    imports: [
        CommonModule,
        NgZorroModule,
        ButtonComponent,
        ReactiveFormsModule,
        NzDatePickerModule,
        FormsModule,
        TableModule,
        NzTimePickerModule,
        NzToolTipModule
    ],
    templateUrl: './timesheet-staff-errorcode.component.html',
    styleUrl: './timesheet-staff-errorcode.component.css',
})
export class TimesheetStaffErrorcodeComponent implements OnInit {

    staffErrorcodesForm!: FormGroup;
    dispositionList: any;
    programName!: string;
    reasonList: any;
    isSubmitted: boolean = false;

    @Input() errorcodeData: any;
    @Input() disabled: boolean = false;
    @Output() onSaveComplete = new EventEmitter<any>();

    constructor(
        private fb: FormBuilder,
        private lookupService: LookupService,
        private timesheetService: TimesheetService,
        private userService: UserService
    ) {
    }

    ngOnInit(): void {
        const selectedProgram = this.userService.getSelectedProgram();
        this.reasonList = this.lookupService.getLookupValue('Disposition Reason');
        this.dispositionList = this.lookupService.getLookupValue('TimeSheet Disposition').filter((e: any) => e.lkpValueCode == 'O' || e.lkpValueCode == 'D');
        this.programName = selectedProgram?.program_code ?? '';
        this.staffErrorcodesForm = this.fb.group({
            errorcodeEntries: this.fb.array([])
        }, { validators: this.atLeastOneDispositionValidator() });

        this.patchErrorcodesData();
    }

    get errorcodeEntries() {
        return this.staffErrorcodesForm.get('errorcodeEntries') as FormArray;
    }

    patchErrorcodesData() {
        if (this.errorcodeData && this.errorcodeData.length) {
            const errorcodeFormArray = this.errorcodeEntries;

            this.errorcodeData.forEach((entry: any) => {
                const entryFormGroup = this.fb.group({
                    id: [entry.id],
                    code: [entry.code],
                    desc: [entry.desc],
                    systemDisposition: [entry.systemDisposition],
                    dispositionLkp: [entry.dispositionLkp],
                    comments: [entry.comments],
                    reasonLkp: [entry.reasonLkp],
                    allowOverride: [entry.allowOverride]
                });

                entryFormGroup.get('dispositionLkp')?.valueChanges.subscribe((dispositionLkp) => {
                    this.applyConditionalValidation(entryFormGroup, dispositionLkp);
                });

                if (this.disabled) {
                    Object.keys(entryFormGroup.controls).forEach(controlName => {
                        const control = entryFormGroup.get(controlName);
                        if (control) {
                            control.disable();
                        }
                    });
                }

                errorcodeFormArray.push(entryFormGroup);
            });
        }
    }

    atLeastOneDispositionValidator() {
        return (control: AbstractControl): ValidationErrors | null => {
            const entries = control.get('errorcodeEntries')?.value;
            if (entries && entries.some((entry: any) => entry.dispositionLkp)) {
                return null; // valid if at least one disposition is selected
            }
            return { atLeastOneDisposition: 'At least one disposition must be selected' };
        };
    }

    getFilteredDispositionList(entry: any) {
        if (!entry.allowOverride) {
            return this.dispositionList.filter((e: any) => e.lkpValueCode != 'O');
        }

        return this.dispositionList;
    }

    onSave() {
        this.isSubmitted = true;
        this.atLeastOneDispositionValidator();
        if (this.staffErrorcodesForm.hasError('atLeastOneDisposition')) {
            return;
        }

        if (this.errorcodeEntries.valid) {
            const payload = {
                programCode: this.programName,
                adjudicationDetails: this.errorcodeEntries.value.map((entry: any) => ({
                    tslineId: entry.id,
                    dispositionLkp: entry.dispositionLkp,
                    comments: entry.comments,
                    reasonLkp: entry.reasonLkp
                }))
            };

            this.timesheetService.saveAdjudicationDetails(payload).subscribe(
                (response: any) => {
                    console.log('Save successful', response);
                    this.onSaveComplete.emit(response);
                },
                (error: any) => {
                    console.error('Error while saving', error);
                }
            );
        }
    }

    applyConditionalValidation(entryFormGroup: any, dispositionLkp: any): void {
        const commentsControl = entryFormGroup.get('comments');
        const reasonControl = entryFormGroup.get('reasonLkp');

        if (dispositionLkp != null) {
            // If dispositionLkp is not null, both comments and reasonLkp must be required
            commentsControl?.setValidators([Validators.required]);
            reasonControl?.setValidators([Validators.required]);
        } else {
            // If dispositionLkp is null, remove validators (optional)
            commentsControl?.setValidators([]);
            reasonControl?.setValidators([]);
        }

        // Trigger form validation manually after updating validators
        commentsControl?.updateValueAndValidity();
        reasonControl?.updateValueAndValidity();
    }
}